import { type Cleanup, composeCleanup } from '../helpers/cleanup'
import { isDraftInputElement, setupDraftInput } from '../helpers/draft-input'
import { setupPageComponent } from '../helpers/webawesome-components'

// Private types ---------------------------------------------------------------

type NumberInputHookContext = {
  readonly el: HTMLElement
  _cleanup?: Cleanup
}

// Private functions -----------------------------------------------------------

// Clears through the same events as typing, including the native form carrier.
const setupNumberInput = (element: HTMLElement): Cleanup => {
  const handleClear = (event: Event): void => {
    const button =
      event.target instanceof Element
        ? event.target.closest('[data-part="number-input-clear"]')
        : null

    if (
      button instanceof HTMLButtonElement &&
      element.contains(button) &&
      !button.disabled &&
      !element.hasAttribute('disabled') &&
      !element.hasAttribute('readonly') &&
      isDraftInputElement(element)
    ) {
      element.value = ''
      element.dispatchEvent(
        new Event('input', { bubbles: true, composed: true })
      )
      element.dispatchEvent(
        new Event('change', { bubbles: true, composed: true })
      )
      element.focus()
    }
  }

  element.addEventListener('click', handleClear)

  return composeCleanup(setupDraftInput(element), () => {
    element.removeEventListener('click', handleClear)
  })
}

// Public hook -----------------------------------------------------------------

export const FacadeNumberInput = {
  // Connects draft and form values once the number input is ready.
  mounted(this: NumberInputHookContext) {
    this._cleanup = setupPageComponent(this.el, setupNumberInput)
  },

  // Releases listeners and pending live-props work.
  destroyed(this: NumberInputHookContext) {
    this._cleanup?.()
    this._cleanup = undefined
  }
}
